import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (phone_email, display_name, password) =>
    apiClient.post('/auth/register', { phone_email, display_name, password }),
  login: (phone_email, password) =>
    apiClient.post('/auth/login', { phone_email, password }),
  twoFaVerify: (two_fa_token, code) =>
    apiClient.post('/auth/2fa-verify', { two_fa_token, code }),
  verifyEmail: (token) => apiClient.post('/auth/verify-email', { token }),
  resendVerification: () => apiClient.post('/auth/resend-verification'),
  forgotPassword: (phone_email) => apiClient.post('/auth/forgot-password', { phone_email }),
  resetPassword: (token, password) => apiClient.post('/auth/reset-password', { token, password }),
  savePhone: (phone_number) => apiClient.post('/auth/save-phone', { phone_number }),
  setup2fa: (phone_number) => apiClient.post('/auth/setup-2fa', { phone_number }),
  verify2faSetup: (code) => apiClient.post('/auth/verify-2fa-setup', { code }),
  disable2fa: () => apiClient.post('/auth/disable-2fa'),
  getMe: () => apiClient.get('/auth/me'),
  updateMe: (data) => apiClient.put('/auth/me', data),
  uploadAvatar: (formData) => apiClient.post('/auth/avatar', formData),
  search: (q) => apiClient.get('/auth/search', { params: { q } }),
  getUser: (userId) => apiClient.get(`/auth/users/${userId}`),
  getUserActivities: (userId) => apiClient.get(`/auth/users/${userId}/activities`),
  inviteByEmail: (email) => apiClient.post('/auth/invite-by-email', { email }),
};

export const activitiesAPI = {
  create: (data) => apiClient.post('/activities', data),
  getById: (id) => apiClient.get(`/activities/${id}`),
  getFeed: (limit = 20, offset = 0, from, to) =>
    apiClient.get('/activities/feed/personal', { params: { limit, offset, from, to } }),
  update: (id, data) => apiClient.put(`/activities/${id}`, data),
  cancel: (id) => apiClient.post(`/activities/${id}/cancel`),
  react: (id, emoji) => apiClient.post(`/activities/${id}/react`, { emoji }),
  discover: (type, limit = 20, offset = 0) =>
    apiClient.get('/activities/discover', { params: { type, limit, offset } }),
  generateInviteLink: (id) => apiClient.post(`/activities/${id}/invite-link`),
  inviteFriends: (id, friend_ids) => apiClient.post(`/activities/${id}/invite-friends`, { friend_ids }),
  getByInviteToken: (token) => apiClient.get(`/activities/join/${token}`),
  rsvp: (id, response_type) =>
    apiClient.post(`/activities/${id}/rsvp`, { response_type }),
};

export const groupsAPI = {
  create: (name, description) =>
    apiClient.post('/groups', { name, description }),
  getAll: () => apiClient.get('/groups'),
  getMembers: (groupId) => apiClient.get(`/groups/${groupId}/members`),
  addMember: (groupId, userId) =>
    apiClient.post(`/groups/${groupId}/members/${userId}`),
  removeMember: (groupId, userId) =>
    apiClient.delete(`/groups/${groupId}/members/${userId}`),
  update: (groupId, data) => apiClient.put(`/groups/${groupId}`, data),
  toggleSharing: (groupId, sharing_enabled) => apiClient.patch(`/groups/${groupId}/sharing`, { sharing_enabled }),
  generateInviteLink: (groupId) => apiClient.post(`/groups/${groupId}/invite-link`),
  getByInviteToken: (token) => apiClient.get(`/groups/join/${token}`),
  joinByToken: (token) => apiClient.post(`/groups/join/${token}`),
  getMessages: (groupId) => apiClient.get(`/groups/${groupId}/messages`),
  sendMessage: (groupId, content) => apiClient.post(`/groups/${groupId}/messages`, { content }),
};

export const connectionsAPI = {
  getAll: () => apiClient.get('/connections'),
  add: (connected_user_id) =>
    apiClient.post('/connections', { connected_user_id }),
  accept: (connectionId) =>
    apiClient.post(`/connections/${connectionId}/accept`),
  delete: (connectionId) =>
    apiClient.delete(`/connections/${connectionId}`),
  findByContacts: (contacts) =>
    apiClient.post('/connections/find-by-contacts', { contacts }),
};

export const closeCircleAPI = {
  getAll: () => apiClient.get('/close-circle'),
  invite: (trusted_user_id) => apiClient.post('/close-circle', { trusted_user_id }),
  accept: (id) => apiClient.post(`/close-circle/${id}/accept`),
  setTitles: (id, show) => apiClient.put(`/close-circle/${id}/titles`, { show }),
  remove: (id) => apiClient.delete(`/close-circle/${id}`),
  getAvailability: (userId) => apiClient.get(`/close-circle/${userId}/availability`),
};

export const preferencesAPI = {
  get: () => apiClient.get('/auth/preferences'),
  update: (data) => apiClient.put('/auth/preferences', data),
  getEmailPrefs: () => apiClient.get('/auth/email-preferences'),
  updateEmailPrefs: (data) => apiClient.patch('/auth/email-preferences', data),
};

export const notificationsAPI = {
  getAll: () => apiClient.get('/notifications'),
  markRead: (id) => apiClient.post(`/notifications/${id}/read`),
  markAllRead: () => apiClient.post('/notifications/read-all'),
};

export const chatAPI = {
  getMessages: (activityId) =>
    apiClient.get(`/chat/activity/${activityId}`),
  postMessage: (activityId, content) =>
    apiClient.post(`/chat/activity/${activityId}`, { content }),
  deleteMessage: (activityId, messageId) =>
    apiClient.delete(`/chat/activity/${activityId}/message/${messageId}`),
};

export const dmAPI = {
  getConversations: () => apiClient.get('/dm/conversations'),
  getMessages: (userId) => apiClient.get(`/dm/${userId}`),
  send: (userId, content) => apiClient.post(`/dm/${userId}`, { content }),
  deleteMessage: (messageId) => apiClient.delete(`/dm/message/${messageId}`),
  deleteConversation: (userId) => apiClient.delete(`/dm/conversation/${userId}`),
};

export const instagramAPI = {
  getUsers: () => apiClient.get('/instagram/users'),
  disconnect: () => apiClient.post('/instagram/disconnect'),
};

export const calendarAPI = {
  getConnections: () => apiClient.get('/calendar/connections'),
  deleteConnection: (id) => apiClient.delete(`/calendar/connections/${id}`),
  getGoogleAuthUrl: (returnTo) => apiClient.get('/calendar/google/auth', { params: returnTo ? { returnTo } : {} }),
  addIcsUrl: (url, label) => apiClient.post('/calendar/ics/url', { url, label }),
  uploadIcs: (formData) => apiClient.post('/calendar/ics/upload', formData),
  getEvents: (from, to) => apiClient.get('/calendar/events', { params: { from, to } }),
  syncConnection: (id) => apiClient.post(`/calendar/sync/${id}`),
};

export default apiClient;
